import './styles.css';

import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { $getRoot, EditorState, LexicalEditor } from 'lexical';
import ReactDOM, { Root } from 'react-dom/client';

import { BubbleConfig, ConfigContext } from './context/ConfigContext';
import { SharedHistoryContext } from './context/SharedHistoryContext';
import Editor from './Editor';
import EditorNodes from './nodes/EditorNodes';
import { TableContext } from './plugins/TablePlugin';
import EditorTheme from './themes/EditorTheme';

async function create(editorConfig: BubbleConfig) {
  const initialConfig = {
    namespace: 'AirEditor',
    nodes: [...EditorNodes],
    onError: (error: Error) => {
      throw error;
    },
    theme: EditorTheme,
    editable: editorConfig.editable,
  };

  return new Promise((resolve) => {
    const render = (root: Root) => (mentionOptions: string[]) => {
      root.render(
        <LexicalComposer initialConfig={initialConfig}>
          <ConfigContext.Provider value={editorConfig}>
            <SharedHistoryContext>
              <TableContext>
                <div
                  className="editor-shell"
                  style={
                    editorConfig.fitToContent
                      ? { height: 'fit-to-content' }
                      : { height: editorConfig.height }
                  }
                >
                  <Editor mentionOptions={mentionOptions} />
                </div>
              </TableContext>
            </SharedHistoryContext>
          </ConfigContext.Provider>
          <OnChangePlugin ignoreSelectionChange={true} onChange={onChange} />
        </LexicalComposer>
      );
    };

    const root = ReactDOM.createRoot(
      document.getElementById(editorConfig.id) as HTMLElement
    );

    const updateMentions = render(root);

    updateMentions([]);

    function onChange(editorState: EditorState, editor: LexicalEditor) {
      editorState.read(() => {
        const root = $getRoot();

        const htmlState = editor.getRootElement()?.innerHTML ?? '';
        const jsonState = JSON.stringify(editorState.clone(null));
        const textState = root.getTextContent();

        //Wait first cycle to return editor instance to make sure it has been initialized
        resolve(editor);

        editorConfig.updateHandler?.(htmlState, jsonState, textState);
      });
    }
  });
}

async function createWithMentions(editorConfig: BubbleConfig) {
  const initialConfig = {
    namespace: 'AirEditor',
    nodes: [...EditorNodes],
    onError: (error: Error) => {
      throw error;
    },
    theme: EditorTheme,
    editable: editorConfig.editable,
  };

  return new Promise((resolve) => {
    const render = (root: Root) => (mentionOptions: string[]) => {
      root.render(
        <LexicalComposer initialConfig={initialConfig}>
          <ConfigContext.Provider value={editorConfig}>
            <SharedHistoryContext>
              <TableContext>
                <div
                  className="editor-shell"
                  style={
                    editorConfig.fitToContent
                      ? { height: 'fit-to-content' }
                      : { height: editorConfig.height }
                  }
                >
                  <Editor mentionOptions={mentionOptions} />
                </div>
              </TableContext>
            </SharedHistoryContext>
          </ConfigContext.Provider>
          <OnChangePlugin ignoreSelectionChange={true} onChange={onChange} />
        </LexicalComposer>
      );
    };

    const root = ReactDOM.createRoot(
      document.getElementById(editorConfig.id) as HTMLElement
    );

    const updateMentions = render(root);

    updateMentions([]);

    function onChange(editorState: EditorState, editor: LexicalEditor) {
      editorState.read(() => {
        const root = $getRoot();

        const htmlState = editor.getRootElement()?.innerHTML ?? '';
        const jsonState = JSON.stringify(editorState.clone(null));
        const textState = root.getTextContent();

        //Wait first cycle to return editor instance to make sure it has been initialized
        resolve([editor, updateMentions]);

        editorConfig.updateHandler?.(htmlState, jsonState, textState);
      });
    }
  });
}

export { create, createWithMentions };
